import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from './api/axiosConfig';
import './App.css';
import { Menu, Container, Dropdown, Icon, Sidebar, SidebarPushable, SidebarPusher, Loader } from 'semantic-ui-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ArcGISAuthProvider } from './contexts/ArcGISAuthContext';
// Eagerly loaded (always needed or lightweight)
import ConnectionStatus from './components/ConnectionStatus';
import BrandHeader from './components/BrandHeader';
import PreLoginSplash from './components/PreLoginSplash';
import Splash from './components/Splash';
import ThemeToggle from './components/ThemeToggle';
import { warmProjectTypes } from './utils/projectTypes';
import { useCreateProjectModals } from './hooks';
// Lazy-loaded (route-based code splitting)
const TableView = lazy(() => import('./components/TableView'));
const QueryEditor = lazy(() => import('./components/QueryEditor'));
const CreateProject = lazy(() => import('./components/CreateProject'));
const CreateProjectChoice = lazy(() => import('./components/CreateProjectChoice'));
const ProjectsList = lazy(() => import('./components/ProjectsList'));
const SiteDetail = lazy(() => import('./components/SiteDetail'));
const ImportProjectFromShapefile = lazy(() => import('./components/ImportProjectFromShapefile'));
const ImportSurvey = lazy(() => import('./components/ImportSurvey'));
const ImportFromUSN = lazy(() => import('./components/ImportFromUSN'));
const SitesInBoundaryModal = lazy(() => import('./components/SitesInBoundaryModal'));
const PendingRefValuesPanel = lazy(() => import('./components/PendingRefValuesPanel'));
const AttributeGroupsPanel = lazy(() => import('./components/AttributeGroupsPanel'));
const AttributeMergePanel = lazy(() => import('./components/AttributeMergePanel'));
const AppSettingsPanel = lazy(() => import('./components/AppSettingsPanel'));
const RefValuesManagementPanel = lazy(() => import('./components/RefValuesManagementPanel'));
const ResearchLocation = lazy(() => import('./components/ResearchLocation'));
const UserManagementPanel = lazy(() => import('./components/UserManagementPanel'));
const UserProfile = lazy(() => import('./components/UserProfile'));

function AppContent() {
  const { isAuthenticated, loading: authLoading, logout, user, isAdmin, isSuperAdmin, canCreateProject, canUseGIS } = useAuth();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [activeView, setActiveView] = useState('splash'); // 'splash', 'tables', 'query', 'projects', 'research-location', 'site-detail'
  const [isLoading, setIsLoading] = useState(true);
  // Projects editing state
  const [editingProject, setEditingProject] = useState(null);
  // Sites detail state
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteNavigationContext, setSiteNavigationContext] = useState(null); // { siteIds: [], currentIndex: 0 }
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Track previous view for navigation
  const [previousView, setPreviousView] = useState(null);
  // Create-project modal state, lifted so it survives view transitions
  // (Add Sites, Site picker, post-boundary Sites-in-Boundary + shapefile flow).
  const {
    addSitesModalOpen, setAddSitesModalOpen,
    siteModalOpen, setSiteModalOpen,
    sitesInBoundaryModalOpen,
    boundaryGeometryForModal,
    pendingShapefileData,
    reset: resetCreateProjectModals,
    openSitesInBoundary,
  } = useCreateProjectModals();

  useEffect(() => {
    if (isAuthenticated) {
      checkConnection();
      warmProjectTypes();
      if (isSuperAdmin()) loadTables();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prevent browser back button from navigating away from the app
  useEffect(() => {
    if (!isAuthenticated) return;

    window.history.pushState({ app: true }, '');

    const handlePopState = () => {
      window.history.pushState({ app: true }, '');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAuthenticated]);

  const checkConnection = async () => {
    try {
      const response = await axios.get('/api/v1/health');
      const data = response.data || {};
      setConnectionStatus({
        ...data,
        _hint: data.database === 'connected' ? null : 'Backend returned database: ' + (data.database || 'missing'),
      });
    } catch (error) {
      const status = error.response?.status;
      const body = error.response?.data;
      const msg = [status && `HTTP ${status}`, body?.error || body?.detail || body?.message, error.message].filter(Boolean).join(' — ');
      setConnectionStatus({
        status: 'error',
        database: 'disconnected',
        _hint: msg || 'Request failed',
      });
    }
  };

  const loadTables = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/v1/tables');
      setTables(response.data.data || []);
    } catch (error) {
      console.error('Failed to load tables:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation helpers for Splash actions

  const createNewSurvey = () => { setEditingProject(null); setActiveView('create-project'); };
  const createProject = () => {
    setEditingProject(null);
    if (canUseGIS()) {
      setActiveView('create-project-choice');
    } else {
      setActiveView('create-project'); // editors go directly to form
    }
  };
  const importProjectFromShapefile = () => setActiveView('import-shapefile');
  const importSitesFromShapefile = () => setActiveView('import-sites-shapefile');
  const importSurvey = () => setActiveView('import-survey');
  const importFromUSN = () => setActiveView('import-usn');
  const researchLocation = () => setActiveView('research-location');
  // Splash "View Buildings" tile lands users in List mode of the merged Search
  // page; ResearchLocation reads this on mount.
  const viewBuildingsList = () => {
    try { sessionStorage.setItem('research-view-mode', 'list'); } catch { /* ignore */ }
    setActiveView('research-location');
  };
  const [projectTypeFilter, setProjectTypeFilter] = useState(null);
  const viewProjects = () => { setProjectTypeFilter(null); setActiveView('projects'); };
  const viewProjectsByType = (type) => { setProjectTypeFilter(type); setActiveView('projects'); };
  const viewSiteDetail = (site, fromView = null, navigationContext = null) => { 
    // Don't close modals — keep them mounted so their state (search criteria,
    // results, pagination) is preserved. The isVisible prop hides the portal
    // during site-detail view so they don't overlay SiteDetail.
    setPreviousView(fromView || activeView);
    setSelectedSite(site); 
    setSiteNavigationContext(navigationContext); // { siteIds: [], currentIndex: 0 }
    setActiveView('site-detail'); 
  };
  const navigateToSite = async (direction) => {
    if (!siteNavigationContext) return;
    const { siteIds, currentIndex } = siteNavigationContext;
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0 || newIndex >= siteIds.length) return;

    try {
      // Fetch the site by ID
      const response = await axios.get(`/api/v1/sites/list?limit=1&offset=0&siteId=${siteIds[newIndex]}`);
      if (response.data.success && response.data.data.length > 0) {
        setSelectedSite(response.data.data[0]);
        setSiteNavigationContext({ siteIds, currentIndex: newIndex });
      }
    } catch (error) {
      console.error('Failed to load site:', error);
    }
  };
  // Switch to a specific site (used when user clicks a sibling building on the
  // SiteDetail map). Keeps siteIds intact and updates currentIndex when known.
  const selectSiteById = async (newSiteId) => {
    try {
      const response = await axios.get(`/api/v1/sites/list?limit=1&offset=0&siteId=${newSiteId}`);
      if (response.data.success && response.data.data.length > 0) {
        setSelectedSite(response.data.data[0]);
        if (siteNavigationContext?.siteIds?.length) {
          const idx = siteNavigationContext.siteIds.findIndex(id => String(id) === String(newSiteId));
          if (idx >= 0) {
            setSiteNavigationContext({ siteIds: siteNavigationContext.siteIds, currentIndex: idx });
          }
        }
      }
    } catch (error) {
      console.error('Failed to load site:', error);
    }
  };
  const backFromSiteDetail = () => {
    if (previousView) {
      // Modals stay mounted with their state; switching back to create-project
      // makes isVisible=true again, which re-shows the modal portal
      setActiveView(previousView);
      setPreviousView(null);
    } else {
      setSelectedSite(null);
      setActiveView('research-location');
    }
    setSiteNavigationContext(null);
  };

  const editProject = (project) => { setEditingProject(project); setActiveView('create-project'); };

  const finishCreateProject = () => {
    resetCreateProjectModals();
    setActiveView('projects');
    setEditingProject(null);
    if (isSuperAdmin()) loadTables(); // GET /tables is super_admin-only
  };

  const handleProjectSaved = (project) => {
    setEditingProject(project);
    // Stay on create-project view — toolbar will now appear
  };

  const handleCreatedWithBoundary = ({ project, geometry, shapefileData }) => {
    setEditingProject(project);
    openSitesInBoundary(geometry, shapefileData);
  };

  const handleNavClick = (view) => {
    setActiveView(view);
    setMobileMenuOpen(false);
  };

  if (authLoading) {
    return (
      <div className="app" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loader active size="large">Checking authentication...</Loader>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app">
        <PreLoginSplash />
      </div>
    );
  }

  const ROLE_LABELS = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    research_gis: 'GIS',
    research_editor: 'Editor',
    research_viewer: 'Viewer',
  };

  return (
    <div className="app">
      <Menu inverted className="app-header" fixed="top">
        <Container fluid className="nav-container">
          {/* BrandHeader component is used for logo */}
          <BrandHeader />

          {/* Desktop Navigation */}
          <Menu.Menu position="left" className="desktop-nav">
            <Menu.Item
              name="home"
              active={activeView === 'splash'}
              onClick={() => handleNavClick('splash')}
              className="nav-item"
            >
              <Icon name="home" />
              <span className="nav-text">Home</span>
            </Menu.Item>
            <Menu.Item
              name="research"
              active={activeView === 'research-location'}
              onClick={() => { handleNavClick('research-location'); researchLocation(); }}
              className="nav-item"
            >
              <Icon name="search" />
              <span className="nav-text">Search</span>
            </Menu.Item>
            {canCreateProject() && (
            <Dropdown
              item
              className="nav-item create-project-dropdown"
              trigger={<span><Icon name="plus circle" /> <span className="nav-text">Create Survey</span></span>}
              pointing="top left"
            >
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => { setMobileMenuOpen(false); createNewSurvey(); }}
                >
                  <Icon name="plus" color="blue" />
                  <span>New Survey</span>
                </Dropdown.Item>
                {canUseGIS() && (
                <>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={() => { setMobileMenuOpen(false); importProjectFromShapefile(); }}
                >
                  <Icon name="upload" color="teal" />
                  <span>Import Boundaries from Shapefile</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => { setMobileMenuOpen(false); importSitesFromShapefile(); }}
                >
                  <Icon name="database" color="green" />
                  <span>Import Buildings from Shapefile</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => { setMobileMenuOpen(false); importSurvey(); }}
                >
                  <Icon name="file excel" style={{ color: '#8e44ad' }} />
                  <span>Import Survey</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => { setMobileMenuOpen(false); importFromUSN(); }}
                >
                  <Icon name="search" color="red" />
                  <span>Import from CRIS USNs</span>
                </Dropdown.Item>
                </>
                )}
              </Dropdown.Menu>
            </Dropdown>
            )}
            <Menu.Item
              name="projects"
              active={activeView === 'projects'}
              onClick={() => { handleNavClick('projects'); viewProjects(); }}
              className="nav-item"
            >
              <Icon name="folder" />
              <span className="nav-text">Surveys</span>
            </Menu.Item>
            {isAdmin() && (
            <Dropdown
              item
              text="Admin"
              className="nav-item utilities-dropdown"
              icon="wrench"
              pointing="left"
            >
              <Dropdown.Menu>
                <Dropdown.Header content="Attributes" />
                <Dropdown.Item
                  active={activeView === 'ref-attributes-editor'}
                  onClick={() => handleNavClick('ref-attributes-editor')}
                >
                  <Icon name="tags" />
                  <span>Attributes Management</span>
                </Dropdown.Item>
                <Dropdown.Item
                  active={activeView === 'attribute-groups'}
                  onClick={() => handleNavClick('attribute-groups')}
                >
                  <Icon name="object group" />
                  <span>Attribute Groups</span>
                </Dropdown.Item>
                <Dropdown.Item
                  active={activeView === 'attribute-merge'}
                  onClick={() => handleNavClick('attribute-merge')}
                >
                  <Icon name="random" />
                  <span>Merge &amp; Delete Attributes</span>
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Header content="Lists" />
                <Dropdown.Item
                  active={activeView === 'ref-values-management'}
                  onClick={() => handleNavClick('ref-values-management')}
                >
                  <Icon name="list" />
                  <span>Lists Management</span>
                </Dropdown.Item>
                <Dropdown.Item
                  active={activeView === 'ref-approvals'}
                  onClick={() => handleNavClick('ref-approvals')}
                >
                  <Icon name="check circle outline" />
                  <span>New List Values</span>
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Header content="Management" />
                <Dropdown.Item
                  active={activeView === 'user-management'}
                  onClick={() => handleNavClick('user-management')}
                >
                  <Icon name="users" />
                  <span>User Management</span>
                </Dropdown.Item>
                {isSuperAdmin() && (
                <Dropdown.Item
                  active={activeView === 'app-settings'}
                  onClick={() => handleNavClick('app-settings')}
                >
                  <Icon name="setting" />
                  <span>App Settings</span>
                </Dropdown.Item>
                )}
                {isSuperAdmin() && (
                <>
                <Dropdown.Divider />
                <Dropdown.Header content="Utilities" />
                <Dropdown.Item
                  active={activeView === 'tables'}
                  onClick={() => { loadTables(); handleNavClick('tables'); }}
                >
                  <Icon name="table" />
                  <span>Edit All Data</span>
                </Dropdown.Item>
                <Dropdown.Item
                  active={activeView === 'query'}
                  onClick={() => handleNavClick('query')}
                >
                  <Icon name="code" />
                  <span>Query Editor</span>
                </Dropdown.Item>
                </>
                )}
              </Dropdown.Menu>
            </Dropdown>
            )}
          </Menu.Menu>

          <Menu.Menu position="right" className="desktop-nav">
            <Menu.Item className="nav-item">
              <ThemeToggle />
            </Menu.Item>
            <Dropdown
              item
              className="nav-item user-dropdown"
              trigger={<span><Icon name="user circle" style={{ color: (connectionStatus?.database ?? connectionStatus?.data?.database) === 'connected' ? '#21ba45' : '#db2828' }} /></span>}
              pointing="top right"
            >
              <Dropdown.Menu>
                <Dropdown.Item disabled style={{ opacity: 1 }}>
                  <Icon name="user" /> {user?.displayName}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handleNavClick('user-profile')}>
                  <Icon name="id card" />
                  <span>Profile</span>
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={logout}>
                  <Icon name="sign out" />
                  <span>Log out</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Menu.Menu>

          {/* Mobile Menu Toggle */}
          <Menu.Menu position="right" className="mobile-nav-toggle">
            <Menu.Item onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <Icon name={mobileMenuOpen ? 'close' : 'sidebar'} size="large" />
            </Menu.Item>
          </Menu.Menu>
        </Container>
      </Menu>

      {/* Mobile Sidebar Menu */}
      <SidebarPushable as="div">
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
        )}
        <Sidebar
          as={Menu}
          animation="overlay"
          icon="labeled"
          inverted
          onHide={() => setMobileMenuOpen(false)}
          vertical
          visible={mobileMenuOpen}
          width="thin"
          className="mobile-sidebar"
        >
          <Menu.Item
            name="home"
            active={activeView === 'splash'}
            onClick={() => handleNavClick('splash')}
          >
            <Icon name="home" />
            Home
          </Menu.Item>
          <Menu.Item
            name="research"
            active={activeView === 'research-location'}
            onClick={() => { handleNavClick('research-location'); researchLocation(); }}
          >
            <Icon name="search" />
            Search
          </Menu.Item>
          {canCreateProject() && (
          <>
          <Menu.Item
            onClick={() => { setMobileMenuOpen(false); createNewSurvey(); }}
          >
            <Icon name="plus" color="blue" />
            New Survey
          </Menu.Item>
          {canUseGIS() && (
          <>
          <Menu.Item header style={{ fontSize: '0.85em', opacity: 0.7, paddingBottom: '0.25rem' }}>
            <Icon name="upload" />
            Import
          </Menu.Item>
          <Menu.Item
            style={{ paddingLeft: '2rem' }}
            onClick={() => { setMobileMenuOpen(false); importProjectFromShapefile(); }}
          >
            <Icon name="upload" color="teal" />
            Import Shapefile
          </Menu.Item>
          <Menu.Item
            style={{ paddingLeft: '2rem' }}
            onClick={() => { setMobileMenuOpen(false); importSitesFromShapefile(); }}
          >
            <Icon name="database" color="green" />
            Import Buildings
          </Menu.Item>
          <Menu.Item
            style={{ paddingLeft: '2rem' }}
            onClick={() => { setMobileMenuOpen(false); importSurvey(); }}
          >
            <Icon name="file excel" style={{ color: '#8e44ad' }} />
            Import Survey
          </Menu.Item>
          <Menu.Item
            style={{ paddingLeft: '2rem' }}
            onClick={() => { setMobileMenuOpen(false); importFromUSN(); }}
          >
            <Icon name="search" color="red" />
            Import CRIS USNs
          </Menu.Item>
          </>
          )}
          </>
          )}
          <Menu.Item
            name="projects"
            active={activeView === 'projects'}
            onClick={() => { handleNavClick('projects'); viewProjects(); }}
          >
            <Icon name="folder" />
            Surveys
          </Menu.Item>
          {isAdmin() && (
          <>
          <Menu.Item header style={{ fontSize: '0.85em', opacity: 0.7, paddingBottom: '0.25rem' }}>
            Attributes
          </Menu.Item>
          <Menu.Item
            name="ref-attributes-editor"
            active={activeView === 'ref-attributes-editor'}
            onClick={() => handleNavClick('ref-attributes-editor')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="tags" />
            Attributes Management
          </Menu.Item>
          <Menu.Item
            name="attribute-groups"
            active={activeView === 'attribute-groups'}
            onClick={() => handleNavClick('attribute-groups')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="object group" />
            Attribute Groups
          </Menu.Item>
          <Menu.Item
            name="attribute-merge"
            active={activeView === 'attribute-merge'}
            onClick={() => handleNavClick('attribute-merge')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="random" />
            Merge &amp; Delete Attributes
          </Menu.Item>
          <Menu.Item header style={{ fontSize: '0.85em', opacity: 0.7, paddingBottom: '0.25rem' }}>
            Lists
          </Menu.Item>
          <Menu.Item
            name="ref-values-management"
            active={activeView === 'ref-values-management'}
            onClick={() => handleNavClick('ref-values-management')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="list" />
            Lists Management
          </Menu.Item>
          <Menu.Item
            name="ref-approvals"
            active={activeView === 'ref-approvals'}
            onClick={() => handleNavClick('ref-approvals')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="check circle outline" />
            New List Values
          </Menu.Item>
          <Menu.Item header style={{ fontSize: '0.85em', opacity: 0.7, paddingBottom: '0.25rem' }}>
            Management
          </Menu.Item>
          <Menu.Item
            name="user-management"
            active={activeView === 'user-management'}
            onClick={() => handleNavClick('user-management')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="users" />
            User Management
          </Menu.Item>
          {isSuperAdmin() && (
          <Menu.Item
            name="app-settings"
            active={activeView === 'app-settings'}
            onClick={() => handleNavClick('app-settings')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="setting" />
            App Settings
          </Menu.Item>
          )}
          {isSuperAdmin() && (
          <>
          <Menu.Item header style={{ fontSize: '0.85em', opacity: 0.7, paddingBottom: '0.25rem' }}>
            Utilities
          </Menu.Item>
          <Menu.Item
            name="tables"
            active={activeView === 'tables'}
            onClick={() => { loadTables(); handleNavClick('tables'); }}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="table" />
            Edit All Data
          </Menu.Item>
          <Menu.Item
            name="query"
            active={activeView === 'query'}
            onClick={() => handleNavClick('query')}
            style={{ paddingLeft: '2rem' }}
          >
            <Icon name="code" />
            Query Editor
          </Menu.Item>
          </>
          )}
          </>
          )}
          <Menu.Item className="mobile-connection-status">
            <ConnectionStatus status={connectionStatus} />
          </Menu.Item>
          <Menu.Item className="nav-item" style={{ opacity: 0.7, fontSize: '0.9em' }}>
            <Icon name="user" />
            {user?.displayName} ({ROLE_LABELS[user?.role] || user?.role})
          </Menu.Item>
          <Menu.Item onClick={logout}>
            <Icon name="sign out" />
            Log out
          </Menu.Item>
        </Sidebar>

        <SidebarPusher dimmed={mobileMenuOpen}>
          <div className="app-body">
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}><Loader active size="large" /></div>}>
        {activeView === 'splash' ? (
          <Splash onCreateProject={createProject} onViewProjects={viewProjects} onViewProjectsByType={viewProjectsByType} onViewSites={viewBuildingsList} onResearchLocation={researchLocation} />
        ) : activeView === 'tables' ? (
          <div className="tables-view">
            <aside className="sidebar">
              <div className="sidebar-header">
                <h2>Tables</h2>
                <button className="refresh-btn" onClick={loadTables} title="Refresh">
                  ↻
                </button>
              </div>
              {isLoading ? (
                <div className="loading">Loading...</div>
              ) : tables.length === 0 ? (
                <div className="empty-state">
                  <p>No tables found</p>
                </div>
              ) : (
                <ul className="tables-list">
                  {tables.map((table) => (
                    <li
                      key={table}
                      className={selectedTable === table ? 'active' : ''}
                      onClick={() => setSelectedTable(table)}
                    >
                      <span className="table-icon">▸</span>
                      {table}
                    </li>
                  ))}
                </ul>
              )}
            </aside>
            <main className="main-content">
              {selectedTable ? (
                <TableView tableName={selectedTable} />
              ) : (
                <div className="welcome-screen">
                  <div className="welcome-content">
                    <h2>Select a table to get started</h2>
                    <p>Choose a table from the sidebar to view and manage its data</p>
                  </div>
                </div>
              )}
            </main>
          </div>
        ) : activeView === 'query' ? (
          <QueryEditor />
        ) : activeView === 'ref-approvals' ? (
          <PendingRefValuesPanel />
        ) : activeView === 'attribute-groups' ? (
          <AttributeGroupsPanel />
        ) : activeView === 'attribute-merge' && isAdmin() ? (
          <AttributeMergePanel />
        ) : activeView === 'ref-values-management' ? (
          <RefValuesManagementPanel />
        ) : activeView === 'ref-attributes-editor' && isAdmin() ? (
          <TableView tableName="ref_attributes" defaultPageSize={250} />
        ) : activeView === 'app-settings' ? (
          <AppSettingsPanel />
        ) : activeView === 'user-management' && isAdmin() ? (
          <UserManagementPanel />
        ) : activeView === 'user-profile' ? (
          <UserProfile />
        ) : activeView === 'projects' ? (
          <ProjectsList onEdit={editProject} onCreate={() => createProject()} onChange={() => { if (isSuperAdmin()) loadTables(); }} initialTypeFilter={projectTypeFilter} onTypeFilterConsumed={() => setProjectTypeFilter(null)} />
        ) : activeView === 'site-detail' && selectedSite ? (
          <SiteDetail
            site={selectedSite}
            onBack={backFromSiteDetail}
            backLabel={previousView === 'create-project' ? '← Back to Survey' : previousView === 'projects' ? '← Back to Surveys' : '← Back to Search'}
            hideSatelliteData={false}
            navigationContext={siteNavigationContext}
            onNavigate={navigateToSite}
            currentProjectId={previousView === 'create-project' ? editingProject?.id : null}
            onOpenProject={editProject}
            onSelectSite={selectSiteById}
          />
        ) : activeView === 'create-project-choice' ? (
          <CreateProjectChoice
            onChooseNewSurvey={createNewSurvey}
            onChooseShapefile={importProjectFromShapefile}
            onChooseImportSites={importSitesFromShapefile}
            onChooseImportSurvey={importSurvey}
            onChooseImportUSN={importFromUSN}
            onCancel={() => setActiveView('splash')}
            canUseGIS={canUseGIS()}
          />
        ) : null}

        {/* Keep CreateProject mounted but hidden when navigating to site-detail from it,
            so AddSitesModal state (selections, filters, results) is preserved */}
        {(activeView === 'create-project' || (activeView === 'site-detail' && previousView === 'create-project')) && (
          <div style={{ display: activeView === 'create-project' ? 'block' : 'none' }}>
            <CreateProject
              project={editingProject}
              canUseGIS={canUseGIS()}
              onCreated={finishCreateProject}
              onCreatedWithBoundary={handleCreatedWithBoundary}
              onProjectSaved={handleProjectSaved}
              onCancel={() => { resetCreateProjectModals(); setActiveView(editingProject ? 'projects' : canUseGIS() ? 'create-project-choice' : 'projects'); setEditingProject(null); }}
              onViewSiteDetail={viewSiteDetail}
              addSitesModalOpen={addSitesModalOpen}
              setAddSitesModalOpen={setAddSitesModalOpen}
              siteModalOpen={siteModalOpen}
              setSiteModalOpen={setSiteModalOpen}
              isVisible={activeView === 'create-project'}
            />
            {editingProject?.id && (
              <SitesInBoundaryModal
                open={sitesInBoundaryModalOpen}
                isVisible={activeView === 'create-project'}
                onClose={finishCreateProject}
                projectId={editingProject.id}
                boundaries={boundaryGeometryForModal ? [{ geometry: boundaryGeometryForModal }] : null}
                onSitesLinked={async () => {
                  // If there's pending shapefile data, apply attributes after BIN import
                  if (pendingShapefileData?.file) {
                    try {
                      const formData = new FormData();
                      formData.append('shapefile', pendingShapefileData.file);
                      if (pendingShapefileData.mapping) {
                        formData.append('attributeMapping', JSON.stringify(pendingShapefileData.mapping));
                      }
                      await axios.post(
                        `/api/v1/projects/${editingProject.id}/apply-shapefile-attributes`,
                        formData,
                        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
                      );
                    } catch (err) {
                      console.error('Failed to apply shapefile attributes:', err);
                    }
                  }
                  finishCreateProject();
                }}
              />
            )}
          </div>
        )}

        {/* Keep ResearchLocation mounted but hidden when navigating to site-detail from it,
            so search criteria, results, and map state are preserved */}
        {(activeView === 'research-location' || (activeView === 'site-detail' && previousView === 'research-location')) && (
          <div style={{ display: activeView === 'research-location' ? 'block' : 'none' }}>
            <ResearchLocation
              onViewProject={editProject}
              onViewSite={viewSiteDetail}
              isVisible={activeView === 'research-location'}
            />
          </div>
        )}

        {activeView === 'import-shapefile' ? (
          <ImportProjectFromShapefile
            mode="boundary"
            onCreatedWithBoundary={({ project, geometry, shapefileData }) => {
              setActiveView('create-project');
              handleCreatedWithBoundary({ project, geometry, shapefileData });
            }}
            onCancel={() => setActiveView('create-project-choice')}
          />
        ) : null}

        {activeView === 'import-survey' ? (
          <ImportSurvey
            onImportComplete={({ projectId }) => {
              editProject({ id: projectId, hub_project_id: projectId });
            }}
            onCancel={() => setActiveView('create-project-choice')}
          />
        ) : null}

        {activeView === 'import-usn' ? (
          <ImportFromUSN
            onImportComplete={({ projectId }) => {
              editProject({ id: projectId, hub_project_id: projectId });
            }}
            onCancel={() => setActiveView('create-project-choice')}
          />
        ) : null}

        {activeView === 'import-sites-shapefile' ? (
          <ImportProjectFromShapefile
            mode="sites"
            onImportComplete={({ projectId }) => {
              editProject({ id: projectId, hub_project_id: projectId });
            }}
            onCancel={() => setActiveView('create-project-choice')}
          />
        ) : null}
          </Suspense>
          </div>

          <footer className="site-footer">
            <Container>
              <div>© City of New York. Landmarks Preservation Commission · <a href="https://www.nyc.gov/site/lpc/index.page" target="_blank" rel="noopener noreferrer">nyc.gov/site/lpc</a></div>
            </Container>
          </footer>
        </SidebarPusher>
      </SidebarPushable>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ArcGISAuthProvider>
        <AppContent />
      </ArcGISAuthProvider>
    </AuthProvider>
  );
}

export default App;
